import imageCompression from 'browser-image-compression';
import { supabase } from '../lib/supabase';
import {
  WEBSITE_MEDIA_PLACEMENT_KEYS,
  WebsiteCampaign,
  WebsiteCommunityGalleryItem,
  WebsitePracticalHighlight,
  WebsiteSpotlightCard,
  WebsiteTestimonial,
  WebsitePracticalHighlightType,
  website_campaign_seed,
  website_community_gallery_item_seed,
  website_practical_highlight_seed,
  website_spotlight_card_seed,
  website_testimonial_seed,
} from '@pigeons/shared-website-media';
import { websiteContentRepository } from '../repositories/websiteContentRepository';

type website_content_image_folder =
  | 'testimonials'
  | 'spotlight-cards'
  | 'community-gallery'
  | 'practical-highlights'
  | 'campaigns';

const WEBSITE_CONTENT_IMAGES_PREFIX = 'website-content';

const WEBSITE_MEDIA_IMAGES_PREFIX = 'website-images';

const WEBSITE_IMAGE_FILENAME_PATTERN = /\.(jpe?g|png|gif|webp|svg)$/i;

export type WebsiteLibraryImageItem = {
  path: string;
  publicUrl: string;
  fileName: string;
};

const list_website_media_folder = async (
  folderPrefix: string
): Promise<WebsiteLibraryImageItem[]> => {
  const { data, error } = await supabase.storage.from('website_media').list(folderPrefix, {
    limit: 200,
    offset: 0,
    sortBy: { column: 'created_at', order: 'desc' },
  });

  if (error) {
    throw error;
  }

  const files = (data ?? []).filter(
    file =>
      Boolean(file.name) &&
      !file.name.includes('/') &&
      WEBSITE_IMAGE_FILENAME_PATTERN.test(file.name)
  );

  return files.map(file => {
    const path = `${folderPrefix}/${file.name}`;
    const { data: urlData } = supabase.storage.from('website_media').getPublicUrl(path);
    return {
      path,
      publicUrl: urlData.publicUrl,
      fileName: file.name,
    };
  });
};

const list_website_admin_library_images = async (): Promise<WebsiteLibraryImageItem[]> => {
  const contentFolders: website_content_image_folder[] = [
    'testimonials',
    'spotlight-cards',
    'community-gallery',
    'practical-highlights',
    'campaigns',
  ];
  const contentPrefixes = contentFolders.map(
    folder => `${WEBSITE_CONTENT_IMAGES_PREFIX}/${folder}`
  );
  const placementPrefixes = WEBSITE_MEDIA_PLACEMENT_KEYS.map(
    key => `${WEBSITE_MEDIA_IMAGES_PREFIX}/${key}`
  );
  const allPrefixes = [...contentPrefixes, ...placementPrefixes];

  const batches = await Promise.all(allPrefixes.map(prefix => list_website_media_folder(prefix)));

  const byPath = new Map<string, WebsiteLibraryImageItem>();
  for (const batch of batches) {
    for (const item of batch) {
      byPath.set(item.path, item);
    }
  }

  return Array.from(byPath.values()).sort((a, b) => b.path.localeCompare(a.path));
};

const get_file_extension = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension || 'jpg';
};

const upload_content_image = async (
  folder: website_content_image_folder,
  file: File
): Promise<string> => {
  const compressedFile = await imageCompression(file, {
    maxSizeMB: 1,
    maxWidthOrHeight: 1800,
    useWebWorker: true,
    initialQuality: 0.82,
  });

  const fileExtension = get_file_extension(file.name);
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExtension}`;
  const filePath = `${WEBSITE_CONTENT_IMAGES_PREFIX}/${folder}/${fileName}`;

  const { error } = await supabase.storage.from('website_media').upload(filePath, compressedFile, {
    upsert: false,
  });

  if (error) {
    throw error;
  }

  const { data } = supabase.storage.from('website_media').getPublicUrl(filePath);
  return data.publicUrl;
};

const get_next_sort_order = (items: { sort_order: number }[]): number => {
  if (items.length === 0) {
    return 10;
  }

  return Math.max(...items.map(item => item.sort_order)) + 10;
};

const build_reorder_payload = (items: { id: string }[]) =>
  items.map((item, index) => ({
    id: item.id,
    sort_order: (index + 1) * 10,
  }));

export const websiteContentService = {
  listTestimonials: (): Promise<WebsiteTestimonial[]> =>
    websiteContentRepository.listTestimonials(),
  listSpotlightCards: (): Promise<WebsiteSpotlightCard[]> =>
    websiteContentRepository.listSpotlightCards(),
  listCommunityGalleryItems: (): Promise<WebsiteCommunityGalleryItem[]> =>
    websiteContentRepository.listCommunityGalleryItems(),
  listPracticalHighlights: (): Promise<WebsitePracticalHighlight[]> =>
    websiteContentRepository.listPracticalHighlights(),

  uploadContentImage: (folder: website_content_image_folder, file: File): Promise<string> =>
    upload_content_image(folder, file),

  listWebsiteAdminLibraryImages: (): Promise<WebsiteLibraryImageItem[]> =>
    list_website_admin_library_images(),

  createTestimonial: (items: WebsiteTestimonial[]): Promise<WebsiteTestimonial> =>
    websiteContentRepository.createTestimonial({
      sort_order: get_next_sort_order(items),
      image_url: null,
      alt_text_fr: null,
      alt_text_en: null,
      person_name: '',
      person_title_fr: '',
      person_title_en: '',
      quote_fr: null,
      quote_en: null,
    }),
  updateTestimonial: (
    id: string,
    payload: Partial<website_testimonial_seed>
  ): Promise<WebsiteTestimonial> => websiteContentRepository.updateTestimonial(id, payload),
  deleteTestimonial: (id: string): Promise<void> => websiteContentRepository.deleteTestimonial(id),
  reorderTestimonials: (items: WebsiteTestimonial[]): Promise<void> =>
    websiteContentRepository.reorderTestimonials(build_reorder_payload(items)),

  createSpotlightCard: (items: WebsiteSpotlightCard[]): Promise<WebsiteSpotlightCard> =>
    websiteContentRepository.createSpotlightCard({
      sort_order: get_next_sort_order(items),
      image_url: null,
      alt_text_fr: null,
      alt_text_en: null,
      title_fr: '',
      title_en: '',
      description_fr: '',
      description_en: '',
      cta_text_fr: '',
      cta_text_en: '',
      cta_link: '',
      open_in_new_tab: false,
    }),
  updateSpotlightCard: (
    id: string,
    payload: Partial<website_spotlight_card_seed>
  ): Promise<WebsiteSpotlightCard> => websiteContentRepository.updateSpotlightCard(id, payload),
  deleteSpotlightCard: (id: string): Promise<void> =>
    websiteContentRepository.deleteSpotlightCard(id),
  reorderSpotlightCards: (items: WebsiteSpotlightCard[]): Promise<void> =>
    websiteContentRepository.reorderSpotlightCards(build_reorder_payload(items)),

  createCommunityGalleryItem: (
    items: WebsiteCommunityGalleryItem[]
  ): Promise<WebsiteCommunityGalleryItem> =>
    websiteContentRepository.createCommunityGalleryItem({
      sort_order: get_next_sort_order(items),
      image_url: null,
      alt_text_fr: null,
      alt_text_en: null,
    }),
  updateCommunityGalleryItem: (
    id: string,
    payload: Partial<website_community_gallery_item_seed>
  ): Promise<WebsiteCommunityGalleryItem> =>
    websiteContentRepository.updateCommunityGalleryItem(id, payload),
  deleteCommunityGalleryItem: (id: string): Promise<void> =>
    websiteContentRepository.deleteCommunityGalleryItem(id),
  reorderCommunityGalleryItems: (items: WebsiteCommunityGalleryItem[]): Promise<void> =>
    websiteContentRepository.reorderCommunityGalleryItems(build_reorder_payload(items)),

  createPracticalHighlight: (
    items: WebsitePracticalHighlight[],
    itemType: WebsitePracticalHighlightType
  ): Promise<WebsitePracticalHighlight> =>
    websiteContentRepository.createPracticalHighlight({
      sort_order: get_next_sort_order(items),
      item_type: itemType,
      image_url: null,
      alt_text_fr: null,
      alt_text_en: null,
      text_fr: itemType === 'text' ? '' : null,
      text_en: itemType === 'text' ? '' : null,
    }),
  updatePracticalHighlight: (
    id: string,
    payload: Partial<website_practical_highlight_seed>
  ): Promise<WebsitePracticalHighlight> =>
    websiteContentRepository.updatePracticalHighlight(id, payload),
  deletePracticalHighlight: (id: string): Promise<void> =>
    websiteContentRepository.deletePracticalHighlight(id),
  reorderPracticalHighlights: (items: WebsitePracticalHighlight[]): Promise<void> =>
    websiteContentRepository.reorderPracticalHighlights(build_reorder_payload(items)),

  listCampaigns: (): Promise<WebsiteCampaign[]> => websiteContentRepository.listCampaigns(),
  createCampaign: (slug: string): Promise<WebsiteCampaign> =>
    websiteContentRepository.createCampaign({
      slug,
      published: false,
      profession: '',
      badge_fr: null,
      badge_en: null,
      headline_fr: '',
      headline_en: '',
      subheadline_fr: null,
      subheadline_en: null,
      hero_image_url: null,
      hero_image_alt_fr: null,
      hero_image_alt_en: null,
      benefit_1_fr: null,
      benefit_1_en: null,
      benefit_2_fr: null,
      benefit_2_en: null,
      benefit_3_fr: null,
      benefit_3_en: null,
      benefit_4_fr: null,
      benefit_4_en: null,
      gallery_image_urls: [],
      cta_text_fr: null,
      cta_text_en: null,
    }),
  updateCampaign: (id: string, payload: Partial<website_campaign_seed>): Promise<WebsiteCampaign> =>
    websiteContentRepository.updateCampaign(id, payload),
  deleteCampaign: (id: string): Promise<void> => websiteContentRepository.deleteCampaign(id),
};
